import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import { $generateNodesFromDOM } from "@lexical/html";
import {
  $createParagraphNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
} from "lexical";

// Change #100362 - content that is not Lexical JSON (plain HTML or plain text,
// e.g. email templates seeded by SQL deltas) used to fail parseEditorState and
// open blank, and saving then wiped it. Import it as HTML instead. Same approach
// as EmailRichText/Editor.js injectHtml: loose inline nodes are wrapped in a
// paragraph, because the root only accepts element or decorator nodes.
const importHtml = (editor, html) => {
  editor.update(() => {
    const root = $getRoot();
    root.clear();

    const dom = new DOMParser().parseFromString(String(html || ""), "text/html");
    const nodes = $generateNodesFromDOM(editor, dom);

    let pending = null;
    const flush = () => {
      if (pending) {
        root.append(pending);
        pending = null;
      }
    };

    (nodes || []).forEach((node) => {
      if ($isElementNode(node) || $isDecoratorNode(node)) {
        flush();
        root.append(node);
      } else {
        if (!pending) pending = $createParagraphNode();
        pending.append(node);
      }
    });
    flush();

    if (root.getChildrenSize() === 0) root.append($createParagraphNode());
  });
};

export const LoadInitialContent = ({ receivedContent }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!receivedContent || !editor) return;

    let editorStateJSONString;
    // The { json, html } wrapper's html half, kept as a fallback if json is unusable
    let wrapperHtml = null;

    try {
      const parsed = JSON.parse(receivedContent);

      // Case 1: Wrapped in { json: "...", html: "..." }
      if (parsed?.json) {
        editorStateJSONString = parsed.json;
        wrapperHtml = parsed?.html || null;
      } else {
        // Case 2: It's a raw editorState string (directly stringified)
        editorStateJSONString = receivedContent;
      }
    } catch (error) {
      // Fallback: maybe it's already the raw string
      editorStateJSONString = receivedContent;
    }

    try {
      const editorState = editor.parseEditorState(editorStateJSONString);
      editor.update(() => {
        editor.setEditorState(editorState);
      });
    } catch (error) {
      // Not Lexical JSON: load it as HTML (plain text works too)
      try {
        importHtml(editor, wrapperHtml ?? receivedContent);
      } catch (importError) {
        console.error("Failed to parse/set editor state", error, importError);
      }
    }
  }, [receivedContent, editor]);

  return null;
};
